import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Embed, Message } from 'semantic-ui-react';
import cx from 'classnames';
import EmbedConsentGate from 'volto-ministerio-publico-federal/components/CookieConsent/EmbedConsentGate';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers/Url/Url';

/**
 * Corpo do bloco Vídeo — cópia do core com UMA mudança: o vídeo toca no
 * primeiro clique.
 *
 * O core entrega `id` + `source` ao `<Embed>` do semantic-ui-react e deixa
 * ele montar a URL. Só que o `getSrc()` dessa versão (2.1.5) junta os
 * parâmetros com `&amp;` LITERAL:
 *
 *     //www.youtube.com/embed/ID?autohide=true&amp;autoplay=false&amp;…
 *
 * O YouTube lê isso como UM parâmetro `autohide` cujo valor é todo o resto,
 * então nenhuma das opções seguintes chega até ele. Era por isso que o
 * primeiro clique só trocava a capa pelo iframe e o vídeo ainda esperava um
 * segundo clique no play do YouTube — e por isso trocar `autoplay` no
 * `embedSettings` do core não resolveria nada.
 *
 * Passando `url` (em vez de `id`/`source`), o `getSrc()` devolve a string
 * como está e podemos usar `&` de verdade, com `autoplay=1`.
 *
 * O restante do arquivo é o do core, incluindo `getVideoIDAndPlaceholder` —
 * copiada, e não importada, porque este shadow SUBSTITUI o módulo original e
 * importá-lo por `@plone/volto/…/Video/Body` traria o próprio arquivo de
 * volta, num ciclo.
 */

//Extracting videoID, listID and thumbnailURL from the video URL
const getVideoIDAndPlaceholder = (url) => {
  let videoID = null;
  let listID = null;
  let thumbnailURL = null;

  if (url) {
    if (url.match('youtu')) {
      if (url.match('list')) {
        const matches = url.match(/^.*\?list=(.*)|^.*&list=(.*)$/);
        listID = matches[1] || matches[2];

        let thumbnailID = null;
        if (url.match(/\?v=(.*)&list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)&list(.*)/)[1];
        }
        if (url.match(/\?v=(.*)\?list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)\?list(.*)/)[1];
        }
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/sddefault.jpg';
      } else if (url.match('live')) {
        videoID = url.match(/^.*\/live\/(.*)/)[1];
      } else if (url.match(/\.be\//)) {
        videoID = url.match(/^.*\.be\/(.*)/)[1];
      } else if (url.match(/\?v=/)) {
        videoID = url.match(/^.*\?v=(.*)$/)[1];
      }

      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        //load video preview image from youtube
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/sddefault.jpg';
      }
    } else if (url.match('vimeo')) {
      videoID = url.match(/^.*\.com\/(.*)/)[1];
      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        thumbnailURL = 'https://vumbnail.com/' + thumbnailID + '.jpg';
      }
    }
  }
  return { videoID, listID, thumbnailURL };
};

/**
 * O id vem do endereço colado pelo editor e pode arrastar o `?si=` que o
 * YouTube acrescenta ao botão Compartilhar. O core já faz essa limpeza para
 * montar a thumbnail; aqui ela também é necessária para a URL do player.
 */
const limparId = (id) => (id || '').split('?')[0].split('&')[0];

/** URL do player do YouTube já com autoplay e sem os relacionados de terceiros. */
const youtubeEmbedUrl = (videoID) =>
  `https://www.youtube.com/embed/${limparId(videoID)}?autoplay=1&rel=0&modestbranding=1`;

const youtubePlaylistEmbedUrl = (listID) =>
  `https://www.youtube.com/embed/videoseries?list=${limparId(listID)}&autoplay=1&rel=0`;

const vimeoEmbedUrl = (videoID) =>
  `https://player.vimeo.com/video/${limparId(videoID)}?autoplay=1&byline=0&portrait=0&title=0`;

/**
 * AB#11160: o portal só aceita vídeo do YouTube (decisão da Coorweb, 03/10:
 * "retirar a opção de subir vídeo; usar somente link do YouTube"). No editor,
 * Vimeo e arquivo .mp4 recebem este aviso em vez do player; na página
 * publicada o que já foi salvo continua tocando, para não quebrar conteúdo
 * antigo.
 */
export const ehYouTube = (url) => Boolean(url && /youtu\.?be/i.test(url));

const AvisoSoYouTube = () => (
  <div>
    <Message warning>
      <center>
        <FormattedMessage
          id="mpf_video_block_so_youtube"
          defaultMessage="O portal só aceita vídeos do YouTube. Publique o vídeo no canal do MPF no YouTube e use o link dele neste bloco."
        />
      </center>
    </Message>
  </div>
);

const Body = ({ data, isEditMode }) => {
  let placeholder = data.preview_image
    ? isInternalURL(data.preview_image)
      ? `${flattenToAppURL(data.preview_image)}/@@images/image`
      : data.preview_image
    : null;

  const { videoID, listID, thumbnailURL } = getVideoIDAndPlaceholder(data.url);

  placeholder = !placeholder ? thumbnailURL : placeholder;

  const ref = React.createRef();
  const onKeyDown = (e) => {
    if (e.nativeEvent.keyCode === 13) {
      ref.current.handleClick();
    }
  };

  const embedSettings = {
    placeholder: placeholder,
    icon: 'play',
    // `defaultActive: false` continua: o quadro abre na capa, e é o clique
    // que troca a capa pelo iframe — que agora já vem tocando.
    defaultActive: false,
    aspectRatio: '16:9',
    tabIndex: 0,
    onKeyPress: onKeyDown,
    ref: ref,
  };

  return (
    <>
      {data.url && isEditMode && !ehYouTube(data.url) ? (
        <AvisoSoYouTube />
      ) : null}
      {data.url && !(isEditMode && !ehYouTube(data.url)) && (
        <div
          className={cx('video-inner', {
            'full-width': data.align === 'full',
          })}
        >
          {data.url.match('youtu') || data.url.match('vimeo') ? (
            <EmbedConsentGate
              provider={data.url.match('youtu') ? 'YouTube' : 'Vimeo'}
              href={data.url}
            >
              {data.url.match('youtu') ? (
                data.url.match('list') ? (
                  <Embed
                    url={youtubePlaylistEmbedUrl(listID)}
                    {...embedSettings}
                  />
                ) : (
                  <Embed url={youtubeEmbedUrl(videoID)} {...embedSettings} />
                )
              ) : (
                <Embed url={vimeoEmbedUrl(videoID)} {...embedSettings} />
              )}
            </EmbedConsentGate>
          ) : (
            <>
              {data.url.match('.mp4') ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  src={
                    isInternalURL(data.url)
                      ? data.url.includes('@@download')
                        ? data.url
                        : `${flattenToAppURL(data.url)}/@@download/file`
                      : data.url
                  }
                  controls
                  poster={placeholder}
                  type="video/mp4"
                />
              ) : isEditMode ? (
                <div>
                  <Message>
                    <center>
                      <FormattedMessage
                        id="Please enter a valid URL by deleting the block and adding a new video block."
                        defaultMessage="Please enter a valid URL by deleting the block and adding a new video block."
                      />
                    </center>
                  </Message>
                </div>
              ) : (
                <div className="invalidVideoFormat" />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

Body.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default Body;
export { getVideoIDAndPlaceholder, youtubeEmbedUrl, vimeoEmbedUrl };
